/**
 * The application shell: navigation rail, routes, and the things every page shares —
 * the command palette, the picks tray and the new-sheet form.
 *
 * Routes are a data router (`createBrowserRouter`), which is what lets a page block
 * navigation while it has unsaved work. The old routes still work: each redirects to
 * where its page lives now.
 */
import {
  Link,
  Navigate,
  NavLink,
  Outlet,
  createBrowserRouter,
  isRouteErrorResponse,
  useLocation,
  useMatch,
  useNavigate,
  useParams,
  useRouteError,
} from 'react-router-dom';
import clsx from 'clsx';
import {
  AlertTriangle,
  BookOpen,
  ChevronsLeft,
  ChevronsRight,
  CircleHelp,
  Command as CommandIcon,
  FileCode2,
  Files,
  Home,
  Layers,
  LogOut,
  Moon,
  Plug,
  Settings2,
  Shield,
  Sun,
} from 'lucide-react';
import { Suspense, lazy, useEffect, useState, type ComponentType } from 'react';
import { useAuth } from './auth';
import { useBasket } from './basket';
import { CommandPalette, Kbd, ShortcutsOverlay } from './commands/CommandPalette';
import { useCommands, useRegistry, CommandsProvider } from './commands/registry';
import { L, WEEK_LABELS } from './labels';
import { exerciseEditPath } from './libraryUrl';
import { prefsSavedLabel, usePrefs } from './prefs';
import { Button, EmptyState, Spinner, useEscapeLayer } from './components/ui';
import { NewSheetForm, type NewSheetDefaults } from './components/NewSheetForm';
import { PicksTray } from './components/PicksTray';
import { LoginPage } from './pages/Login';
// Phase 7, Lane O: "Open view: …" in the command palette
import { SavedViewCommands } from './components/views/SavedViews';
import { browserLocale, LOCALE_NAMES, LOCALES, tx, lc } from './i18n';

/*
 * One page per chunk. Only the login page stays eager: it is what an unauthenticated
 * visitor sees, and lazy-loading it would add a round trip before anything appears.
 */
const page = <K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K) =>
  lazy(() => load().then((m) => ({ default: m[name] })));
const DashboardPage = page(() => import('./pages/Dashboard'), 'DashboardPage');
const LibraryPage = page(() => import('./pages/Library'), 'LibraryPage');
const SheetsPage = page(() => import('./pages/Sheets'), 'SheetsPage');
const SheetEditorPage = page(() => import('./pages/SheetEditor'), 'SheetEditorPage');
const ArchivePage = page(() => import('./pages/Archive'), 'ArchivePage');
const AuthoringPage = page(() => import('./pages/Authoring'), 'AuthoringPage');
const ApiAccessPage = page(() => import('./pages/ApiAccess'), 'ApiAccessPage');
const HeadersPage = page(() => import('./pages/Headers'), 'HeadersPage');
const ReviewPage = page(() => import('./pages/Review'), 'ReviewPage');
const AdminPage = page(() => import('./pages/Admin'), 'AdminPage');
const HelpPage = page(() => import('./pages/Help'), 'HelpPage');

interface NavEntry {
  to: string;
  label: string;
  icon: ComponentType<{ size?: number }>;
  end?: boolean;
  keys?: string;
  /** listed for admins only (the server refuses everyone else regardless) */
  adminOnly?: boolean;
}
/**
 * Grouped by what a tutor is doing; the advanced pages are folded away. A function, so
 * the words are the current language's (see i18n/index.ts).
 */
const navGroups = (): { group: string | null; entries: NavEntry[]; advanced?: boolean }[] => [
  { group: null, entries: [{ to: '/', label: L.home, icon: Home, end: true, keys: 'g h' }] },
  {
    group: tx('Make'),
    entries: [
      { to: '/sheets', label: L.sheets, icon: Files, keys: 'g s' },
      /*
       * One place for exercises, whatever their source: it replaced "Find exercises" and
       * "My exercises", which were the same exercises under two names.
       */
      { to: '/exercises', label: L.exercises, icon: BookOpen, keys: 'g e' },
    ],
  },
  { group: tx('Check'), entries: [{ to: '/review', label: L.review, icon: AlertTriangle, keys: 'g r' }] },
  {
    group: tx('Advanced'),
    advanced: true,
    entries: [
      { to: '/templates', label: L.templates, icon: FileCode2 },
      { to: '/integrations', label: L.integrations, icon: Plug },
      { to: '/admin', label: WEEK_LABELS.admin, icon: Shield, adminOnly: true },
    ],
  },
];

const Loading = () => (
  <div className="flex h-full items-center justify-center text-[var(--ink-faint)]">
    <Spinner size={22} />
  </div>
);

/** An old route whose page moved: keeps the rest of the path, so deep links still work. */
function Moved({ to }: { to: string }) {
  const params = useParams();
  const { search } = useLocation();
  const rest = params['*'] ? `/${params['*']}` : '';
  const id = params.id ? `/${params.id}` : '';
  return <Navigate to={`${to}${id}${rest}${search}`} replace />;
}

/**
 * Where the library was (`/library`, `/library/<id>`): the same address under the Exercises
 * page. The path is taken as typed, still encoded — an id ending in `#<n>` must stay one.
 */
function LibraryMoved() {
  const { pathname, search } = useLocation();
  return <Navigate to={`/exercises${pathname.replace(/^\/library/, '')}${search}`} replace />;
}

/** My exercises (`/write`, `/write?id=<id>`): the Exercises page's own exercises, or the one's editor. */
function WriteMoved() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const id = params.get('id');
  if (id) return <Navigate to={exerciseEditPath(id)} replace />;
  return <Navigate to="/exercises?source=authored" replace />;
}

/** The shell around every signed-in page. */
function Shell() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { prefs, set } = usePrefs();
  const basket = useBasket();
  const { setPaletteOpen, setOverlayOpen } = useRegistry();
  const [newSheet, setNewSheet] = useState<NewSheetDefaults | null>(null);

  // pages ask for the new-sheet form through the URL, so a link can open it — once the
  // user is known, so the request survives a sign-in round trip
  useEffect(() => {
    if (loading || !user) return;
    const params = new URLSearchParams(location.search);
    if (params.get('new') !== '1') return;
    setNewSheet({ startFrom: params.get('from') === 'picks' ? 'picks' : undefined });
    params.delete('new');
    params.delete('from');
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  }, [location.search, location.pathname, navigate, loading, user]);

  // the router's own matcher, so a trailing slash or anything else counts the same way
  const editorMatch = useMatch('/sheets/:id');
  const isEditor = Boolean(editorMatch && editorMatch.params.id !== 'past');
  // a phone has no room for a 240px rail next to the page
  const narrow = useMediaQuery('(max-width: 767px)');
  const collapsed = prefs.railCollapsed || isEditor || narrow;

  useCommands([
    { id: 'palette.open', title: tx('Command palette'), group: tx('General'), keys: 'mod+k', run: () => setPaletteOpen(true) },
    { id: 'help.shortcuts', title: tx('Keyboard shortcuts'), group: tx('General'), keys: 'shift+?', run: () => setOverlayOpen(true) },
    { id: 'rail.toggle', title: tx('Collapse or expand the navigation'), group: tx('General'), keys: 'mod+\\', run: () => set('railCollapsed', !prefs.railCollapsed) },
    { id: 'sheet.new', title: L.newSheet, group: tx('Sheets'), keywords: 'create make', run: () => setNewSheet({}) },
    { id: 'picks.open', title: tx('Open {picks}', { picks: lc(L.picks) }), group: tx('Sheets'), keys: 'g p', run: () => basket.setTrayOpen(true) },
    { id: 'go.past', title: L.pastTerms, group: tx('Go to'), run: () => navigate('/sheets/past') },
    { id: 'go.help', title: L.help, group: tx('Go to'), run: () => navigate('/help') },
    ...navGroups().flatMap((g) =>
      g.entries.filter((e) => !e.adminOnly || user?.role === 'admin').map((e) => ({
        id: `go.${e.to}`,
        title: e.label,
        group: tx('Go to'),
        keys: e.keys,
        run: () => navigate(e.to),
      })),
    ),
  ]);

  if (loading) return <Loading />;
  if (!user) return <LoginPage />;

  return (
    <div className="flex h-full">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-[80] focus:rounded focus:bg-[var(--surface)] focus:px-3 focus:py-2"
      >
        {tx('Skip to content')}
      </a>
      <Rail collapsed={collapsed} canExpand={!isEditor && !narrow} />
      <main id="main" className="flex min-w-0 flex-1 flex-col">
        <Suspense fallback={<Loading />}>
          <Outlet />
        </Suspense>
      </main>
      <PicksTray />
      <CommandPalette />
      <ShortcutsOverlay />
      <NewSheetForm open={newSheet !== null} onClose={() => setNewSheet(null)} defaults={newSheet ?? {}} />
      <SavedViewCommands />
    </div>
  );
}

/** Whether a media query matches, kept up to date. */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

function Rail({ collapsed, canExpand }: { collapsed: boolean; canExpand: boolean }) {
  const { user, logout } = useAuth();
  const { prefs, set } = usePrefs();
  const basket = useBasket();
  const { setPaletteOpen } = useRegistry();
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));
  const [showAdvanced, setShowAdvanced] = useState(prefs.expandAdvanced);
  // open again after a language change, which re-mounts the shell (see LocaleProvider)
  const [prefsOpen, setPrefsOpen] = useState(() => Date.now() < reopenPrefsUntil);
  const { pathname } = useLocation();
  const isAdmin = user?.role === 'admin';
  const nav = navGroups();
  const advancedActive = nav.some((g) => g.advanced && g.entries.some((e) => pathname.startsWith(e.to)));

  const toggleTheme = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle('dark', next);
    try {
      localStorage.setItem('studio-theme', next ? 'dark' : 'light');
    } catch {
      /* not remembered, still applied */
    }
  };

  const itemClass = (isActive: boolean) =>
    clsx(
      'flex items-center gap-2.5 rounded-lg py-2 text-sm transition-colors',
      collapsed ? 'justify-center px-0' : 'px-2.5',
      isActive
        ? 'bg-[color-mix(in_srgb,var(--color-brand-600)_12%,transparent)] font-medium text-[var(--color-brand-600)] dark:text-brand-200'
        : 'text-[var(--ink-soft)] hover:bg-[var(--surface-muted)] hover:text-[var(--ink)]',
    );

  return (
    <nav
      aria-label={tx('Main')}
      className={clsx(
        'flex shrink-0 flex-col border-r border-[var(--line)] bg-[var(--surface)] transition-[width]',
        collapsed ? 'w-14' : 'w-60',
      )}
    >
      <div className={clsx('flex items-center gap-2.5 py-3', collapsed ? 'justify-center' : 'px-4')}>
        <Link to="/" aria-label={tx('Sheet Studio home')}>
          <Logo size={collapsed ? 26 : 30} />
        </Link>
        {!collapsed && (
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold leading-tight">Sheet Studio</div>
            <div className="truncate text-xs text-[var(--ink-faint)]">{tx('Programming 1')}</div>
          </div>
        )}
      </div>

      <div className={clsx('space-y-1 pb-2', collapsed ? 'px-1.5' : 'px-2')}>
        <button
          className={clsx(itemClass(false), 'w-full border border-[var(--line)]')}
          onClick={() => setPaletteOpen(true)}
          title={tx('Search or run a command')}
          aria-label={tx('Search or run a command')}
        >
          <CommandIcon size={16} />
          {!collapsed && (
            <>
              <span className="flex-1 text-left">{tx('Search or run…')}</span>
              <Kbd keys="mod+k" />
            </>
          )}
        </button>
        <button
          className={clsx(itemClass(false), 'w-full')}
          onClick={() => basket.setTrayOpen(true)}
          title={`${L.picks} (${basket.ids.length})`}
          aria-label={`${L.picks}, ${basket.ids.length}`}
        >
          <span className="relative">
            <Layers size={17} />
            {collapsed && basket.ids.length > 0 && (
              <span className="absolute -right-2 -top-2 rounded-full bg-[var(--color-accent-600)] px-1 text-[10px] text-white">
                {basket.ids.length}
              </span>
            )}
          </span>
          {!collapsed && (
            <>
              <span className="flex-1 text-left">{L.picks}</span>
              <span className="chip">{basket.ids.length}</span>
            </>
          )}
        </button>
      </div>

      <div className={clsx('scroll-y flex flex-1 flex-col gap-0.5', collapsed ? 'px-1.5' : 'px-2')}>
        {nav.map((g) => {
          const open = !g.advanced || showAdvanced || advancedActive || collapsed;
          return (
            <div key={g.group ?? 'top'} className="mb-1">
              {g.group && !collapsed && (
                g.advanced ? (
                  <button
                    className="label flex w-full items-center justify-between px-2.5 pb-1 pt-2"
                    aria-expanded={open}
                    onClick={() => setShowAdvanced((s) => !s)}
                  >
                    {g.group} <span aria-hidden>{open ? '−' : '+'}</span>
                  </button>
                ) : (
                  <div className="label px-2.5 pb-1 pt-2">{g.group}</div>
                )
              )}
              {open &&
                g.entries.filter((e) => !e.adminOnly || isAdmin).map(({ to, label, icon: Icon, end }) => (
                  <NavLink key={to} to={to} end={end} title={collapsed ? label : undefined} aria-label={collapsed ? label : undefined} className={({ isActive }) => itemClass(isActive)}>
                    <Icon size={17} />
                    {!collapsed && label}
                  </NavLink>
                ))}
            </div>
          );
        })}
      </div>

      <div className={clsx('relative space-y-0.5 border-t border-[var(--line)] py-2', collapsed ? 'px-1.5' : 'px-2')}>
        <NavLink to="/help" className={({ isActive }) => itemClass(isActive)} title={collapsed ? L.help : undefined} aria-label={collapsed ? L.help : undefined}>
          <CircleHelp size={17} />
          {!collapsed && L.help}
        </NavLink>
        <button className={clsx(itemClass(prefsOpen), 'w-full')} onClick={() => setPrefsOpen((o) => !o)} aria-expanded={prefsOpen} title={tx('Preferences')} aria-label={tx('Preferences')}>
          <Settings2 size={17} />
          {!collapsed && tx('Preferences')}
        </button>
        {prefsOpen && <PrefsPanel onClose={() => setPrefsOpen(false)} />}
        <div className={clsx('flex items-center gap-1 pt-1', collapsed ? 'flex-col' : 'px-1')}>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{user?.name}</div>
              <div className="truncate text-xs text-[var(--ink-faint)]">{user?.role === 'admin' ? tx('admin') : user?.email}</div>
            </div>
          )}
          <button className="btn btn-icon btn-ghost" onClick={toggleTheme} title={dark ? tx('Light theme') : tx('Dark theme')} aria-label={tx('Toggle theme')}>
            {dark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          {canExpand && (
            <button
              className="btn btn-icon btn-ghost"
              onClick={() => set('railCollapsed', !prefs.railCollapsed)}
              title={collapsed ? tx('Expand the navigation') : tx('Collapse the navigation')}
              aria-label={collapsed ? tx('Expand the navigation') : tx('Collapse the navigation')}
            >
              {collapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
            </button>
          )}
          <button className="btn btn-icon btn-ghost" onClick={() => void logout()} title={tx('Sign out')} aria-label={tx('Sign out')}>
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </nav>
  );
}

/** Until when a re-mounted shell opens the Preferences again: just after a language change. */
let reopenPrefsUntil = 0;

function PrefsPanel({ onClose }: { onClose: () => void }) {
  const { prefs, set, expert, setExpert, status } = usePrefs();
  const setLanguage = (value: string) => {
    reopenPrefsUntil = Date.now() + 5000;
    set('locale', value === 'en' || value === 'de' ? value : undefined);
  };
  useEscapeLayer(true, onClose);
  const toggle = (key: 'expandAdvanced' | 'technicalDetails' | 'sourceFirst', label: string, hint: string) => (
    <label className="flex cursor-pointer items-start gap-2 text-sm">
      <input type="checkbox" className="mt-1" checked={prefs[key]} onChange={(e) => set(key, e.target.checked)} />
      <span>
        {label}
        <span className="block text-xs text-[var(--ink-faint)]">{hint}</span>
      </span>
    </label>
  );
  return (
    <div role="dialog" aria-label={tx('Preferences')} className="card absolute bottom-full left-2 z-50 mb-2 w-72 space-y-3 p-3 shadow-lg">
      <div>
        <h2 className="text-sm font-semibold">{tx('Preferences')}</h2>
        <span className="block text-[11px] text-[var(--ink-faint)]" data-testid="prefs-saved">
          {prefsSavedLabel(status)}
        </span>
      </div>
      <label className="flex items-center justify-between gap-2 text-sm">
        {tx('Density')}
        <select className="input h-8 w-36 py-0" value={prefs.density} onChange={(e) => set('density', e.target.value as 'comfortable' | 'compact')}>
          <option value="comfortable">{tx('Comfortable')}</option>
          <option value="compact">{tx('Compact')}</option>
        </select>
      </label>
      <label className="flex items-center justify-between gap-2 text-sm">
        {/* after Density: the panel's first select stays the one it always was */}
        {tx('Language')}
        <select
          className="input h-8 w-36 py-0"
          value={prefs.locale ?? ''}
          onChange={(e) => setLanguage(e.target.value)}
          data-testid="prefs-language"
        >
          <option value="">{tx('Browser ({language})', { language: LOCALE_NAMES[browserLocale()] })}</option>
          {LOCALES.map((l) => (
            <option key={l} value={l} lang={l}>
              {LOCALE_NAMES[l]}
            </option>
          ))}
        </select>
      </label>
      {toggle('expandAdvanced', tx('Open “More options” by default'), tx('Advanced fields in forms start unfolded.'))}
      {toggle('technicalDetails', tx('Show technical details'), tx('Ids, file paths, build times and cache hits.'))}
      {toggle('sourceFirst', tx('Open editors on the source'), tx('Show LaTeX/Typst source before the form.'))}
      <Button size="sm" className="w-full" onClick={() => setExpert(!expert)}>
        {expert ? tx('Back to the defaults') : tx('Expert mode: turn all of these on')}
      </Button>
    </div>
  );
}

function NotFound() {
  return (
    <EmptyState
      icon={<AlertTriangle size={28} />}
      title={tx('There is no page here')}
      hint={tx('The link may be old. Everything is reachable from the navigation on the left.')}
      action={
        <Link className="btn btn-primary" to="/">
          {tx('Go home')}
        </Link>
      }
    />
  );
}

/**
 * Shown when a page fails to load or render. After a deploy, an open tab still asks for
 * the old bundle's chunks, which no longer exist — that is the common case, and a reload
 * fixes it, so it is the first thing offered.
 */
function RouteError() {
  const error = useRouteError();
  const stale =
    error instanceof Error && /dynamically imported module|Importing a module script failed|Failed to fetch/i.test(error.message);
  const message = isRouteErrorResponse(error) ? `${error.status} ${error.statusText}` : error instanceof Error ? error.message : String(error);
  return (
    <div className="flex h-full items-center justify-center p-6">
      <EmptyState
        icon={<AlertTriangle size={28} />}
        title={stale ? tx('Sheet Studio was updated') : tx('Something went wrong on this page')}
        hint={stale ? tx('Reload to get the new version. Nothing you saved is lost.') : message}
        action={<Button variant="primary" onClick={() => window.location.reload()}>{tx('Reload')}</Button>}
      />
    </div>
  );
}

export function Logo({ size = 30 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden>
      <rect width="32" height="32" rx="7" fill="var(--color-brand-600)" />
      <path
        d="M9 9h11a4 4 0 0 1 0 8H9zM9 17h13a4 4 0 0 1 0 7H9z"
        fill="none"
        stroke="var(--color-camel-500)"
        strokeWidth="2.2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Root() {
  return (
    <CommandsProvider>
      <Shell />
    </CommandsProvider>
  );
}

export const router = createBrowserRouter([
  {
    element: <Root />,
    errorElement: <RouteError />,
    children: [
      {
        errorElement: <RouteError />,
        children: [
          { path: '/', element: <DashboardPage /> },
          { path: '/exercises', element: <LibraryPage /> },
          // the editor of an exercise written in Studio (ids `user/<slug>`)
          { path: '/exercises/user/:slug/edit', element: <AuthoringPage /> },
          { path: '/exercises/:id/*', element: <LibraryPage /> },
          { path: '/sheets', element: <SheetsPage /> },
          { path: '/sheets/past', element: <ArchivePage /> },
          { path: '/sheets/past/:id/*', element: <ArchivePage /> },
          { path: '/sheets/:id', element: <SheetEditorPage /> },
          { path: '/review', element: <ReviewPage /> },
          // admins only: the page itself says so to anyone else, and the server enforces it
          { path: '/admin', element: <AdminPage /> },
          { path: '/templates', element: <HeadersPage /> },
          { path: '/integrations', element: <ApiAccessPage /> },
          { path: '/help', element: <HelpPage /> },
          // signed in already (a bookmark, a debug login, an SSO error with a live session)
          { path: '/login', element: <Navigate to="/" replace /> },
          // where pages used to live
          { path: '/library', element: <LibraryMoved /> },
          { path: '/library/*', element: <LibraryMoved /> },
          { path: '/write', element: <WriteMoved /> },
          { path: '/archive', element: <Moved to="/sheets/past" /> },
          { path: '/archive/:id/*', element: <Moved to="/sheets/past" /> },
          { path: '/issues', element: <Moved to="/review" /> },
          { path: '/headers', element: <Moved to="/templates" /> },
          { path: '/access', element: <Moved to="/integrations" /> },
          { path: '*', element: <NotFound /> },
        ],
      },
    ],
  },
]);
